import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames/bind';
import { Button, ChevronRightIcon } from 'tuui';
import { CloseIcon, TOOLTIP_POSITION, TooltipV2 as Tooltip } from 'taboola-ultimate-ui';
import { ExternalLink } from 'components';
import { useNavigate } from 'hooks';
import { generateCreativeEditorPath } from 'modules/campaigns/services/utils';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './craTagsTooltip.module.scss';

const classNameBuilder = classnames.bind(styles);

const msgIdPrefix = 'app.campaigns.creative.tags';
const GTM_PREFIX = 'CRA_Tags: ';
const EMPTY_TAGS = {};
const EMPTY_PUBLISHERS = [];
const helpCenterLink = 'https://www.taboola.com/help/en/articles/9922446-limited-supply-access';
const tagToArticleIdMap = {
    APPLE_COMPETITOR_PRODUCT_AND_SERVICE: 'h_6178e00fb2',
    APPLE_COMPETITOR_PRODUCTS_OR_SERVICES: 'h_6178e00fb2',
    APPLE_BLOCK: 'h_b9c665efee',
    STREAMING_SERVICE: 'h_a30d9ceac3',
    DIGITAL_DEVICE: 'h_6250da17f5',
    INGESTIBLE_CBD: 'h_afd28b589a',
    WEIGHT_LOSS_SUPPLEMENT: 'h_d4b8602e9a',
    CELEBRITY_WEIGHT_LOSS: 'h_a9e6f1089e',
    APPLE_BLOCK_BACKFILL: 'h_38c3a25219',
    APPLE_PRODUCT_AND_SERVICE: 'h_53109b1e4a',
    APPLE_PRODUCTS_OR_SERVICES: 'h_53109b1e4a',
    APPLE_GRAMMAR_UNSAFE: 'h_74522ee005',
    APPLE_GRAMMAR: 'h_74522ee005',
    APPLE_DISTASTEFUL_IMAGE: 'h_3cf960cb8d',
    APPLE_GRAINY_IMAGE: 'h_b201a8aac5',
    APPLE_REVIEW_REJECTED: 'h_85e3c49cf8',
    DISTASTEFUL_IMAGE: 'h_3cf960cb8d',
    CLOSE_UP_BODY_PARTS: 'h_3d84814987',
    MATURE_THEMES: 'h_271c71a793',
    AGGRESSIVE_PROMISES: 'h_810f880c71',
};

export const CRATagsTooltip = ({
    tags = EMPTY_TAGS,
    publishers = EMPTY_PUBLISHERS,
    accountName,
    campaignId,
    creativeId,
    initialSelected,
    onClose,
    showing,
}) => {
    const [selected, setSelected] = useState(initialSelected);
    const navigate = useNavigate();

    useEffect(() => {
        setSelected(initialSelected);
    }, [initialSelected]);

    const navigateToAdsEditDrawer = () => {
        navigate(generateCreativeEditorPath({ accountId: accountName, campaignId, creativeId }));
    };

    const tagContent = useMemo(() => {
        const selectedTags = tags[selected] || [];
        return Array.from(selectedTags)
            .sort((a, b) => (a.tag || '').localeCompare(b.tag || ''))
            .map(t => (
                <ExternalLink
                    key={t.tag}
                    className={styles['link']}
                    href={`${helpCenterLink}#${tagToArticleIdMap[t.tag] || 'h_9df4ca879e'}`}
                >
                    <FormattedMessage
                        id={`${msgIdPrefix}.content.tag.reason.${t.tag}`}
                        defaultMessage={t.tag}
                        className={styles['reason']}
                    >
                        {message => (
                            <>
                                {message}
                                <Tooltip
                                    interactive
                                    position={TOOLTIP_POSITION.RIGHT}
                                    className={styles['reason-tooltip']}
                                    tailHeight={6}
                                    tailStyle={{
                                        zIndex: 1,
                                    }}
                                >
                                    <ExternalLink
                                        href={`${helpCenterLink}#${tagToArticleIdMap[t.tag] || 'h_9df4ca879e'}`}
                                    >
                                        {t.comment || (
                                            <FormattedMessage
                                                id={`${msgIdPrefix}.content.tag.description.${t.tag}`}
                                                defaultMessage="Please review the help documentation for more details"
                                            />
                                        )}
                                    </ExternalLink>
                                </Tooltip>
                            </>
                        )}
                    </FormattedMessage>
                </ExternalLink>
            ));
    }, [tags, selected]);

    return (
        <Tooltip
            interactive
            position={TOOLTIP_POSITION.RIGHT}
            className={styles['tooltip']}
            maskClassName={styles['tooltip-mask']}
            tailClassName={styles['tooltip-tail']}
            tailHeight={9}
            tailStyle={{
                zIndex: 1,
            }}
            showing={showing}
        >
            <div className={styles['container']}>
                <div className={styles['header']}>
                    <div className={styles['title']}>
                        <FormattedMessage id={`${msgIdPrefix}.v2.header.title`} defaultMessage="Unlock More Reach" />
                        <Button className={styles['close-button']} onClick={onClose} aria-label="Close tooltip">
                            <CloseIcon className={styles['close-icon']} />
                        </Button>
                    </div>
                    <FormattedMessage
                        id={`${msgIdPrefix}.v2.header.description`}
                        defaultMessage="This ad has the potential to appear across additional premium publisher groups. Update your ad based on the points below to qualify for broader distribution."
                    />
                </div>
                <div className={styles['content']}>
                    <div className={styles['publishers-container']}>
                        <div className={styles['publishers-header']}>
                            <FormattedMessage
                                id={`${msgIdPrefix}.v2.content.publishers.title`}
                                defaultMessage="Publishers:"
                                className={styles['content-title']}
                            />
                        </div>
                        <div className={styles['publishers-list']} data-testid="cra-tags-publishers">
                            {publishers.map(groupId => {
                                return (
                                    <div
                                        key={groupId}
                                        className={classNameBuilder('publisher', {
                                            selected: selected === groupId,
                                        })}
                                        onClick={() => setSelected(groupId)}
                                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                                        data-metrics-component={`${GTM_PREFIX}Affected Publisher Clicked`}
                                        data-metrics-value={groupId}
                                    >
                                        <FormattedMessage
                                            id={`${msgIdPrefix}.content.publisher.${groupId}.label`}
                                            defaultMessage={groupId}
                                        />
                                        <div>
                                            <ChevronRightIcon className={styles['chevron']} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles['divider']} />
                    <div className={styles['reasons-container']}>
                        <FormattedMessage
                            id={`${msgIdPrefix}.v2.content.reasons.title`}
                            defaultMessage="Special Policy Guidelines:"
                            className={styles['content-title']}
                        />
                        <div className={styles['reasons-list']} data-testid="cra-tags-reasons">
                            {tagContent}
                        </div>
                    </div>
                </div>
                <div className={styles['footer']}>
                    <ExternalLink className={styles['link']} href={helpCenterLink}>
                        <FormattedMessage id={`${msgIdPrefix}.v2.footer.help.link`} defaultMessage="Learn More" />
                    </ExternalLink>
                    <Button onClick={navigateToAdsEditDrawer}>
                        <FormattedMessage id={`${msgIdPrefix}.footer.button`} defaultMessage="Edit Ad" />
                    </Button>
                </div>
            </div>
        </Tooltip>
    );
};
